import type { FC } from "react";
import { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import TextareaAutosize from "react-textarea-autosize";
import { twMerge } from "tailwind-merge";

import { AttachmentChip } from "./AttachmentChip";

import { collaborationUploadFile } from "api/api-worksheets";
import { AIServerFileType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ArrowRightIcon, PaperClipLinkIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { toastWarningMessage } from "utils/alerts";
import { rollbarAndLogError } from "utils/logger";

const ACCEPTED_AI_FILE_TYPES = {
  "image/png": [],
  "image/jpeg": [],
  "image/gif": [],
  "image/webp": [],
  "application/pdf": [],
};

const MAX_FILE_SIZE_MB = 5;

type PendingFile = {
  id: string;
  file: File;
  url?: string;
  uploading: boolean;
};

interface FormInput {
  text: string;
}

export type ChatComposeProps = {
  className?: string;
  onSend?: (text: string, files?: AIServerFileType[]) => void;
  placeholder?: string;
  /**
   * Optional key for form persistence.
   * If provided, the form state will be saved to localStorage under this key.
   * Like controlled/uncontrolled components, this should NEVER change during the component's lifetime.
   */
  persistKey?: string;
  /**
   * Context in which the chat compose is being used.
   * - "class": Used in class chat, requires canCreateChatMessage capability
   * - "ai": Used in AI chat, always allows sending messages
   */
  context?: "class" | "ai";
};

let nextFileId = 0;
const getNextFileId = () => {
  nextFileId += 1;
  return String(nextFileId);
};

/*
 * Compose component for the chat.
 * Note that this is used for both the class chat and the AI chat,
 * which have different requirements.
 * Any user who has access to the AI chat should be able to send messages,
 * whereas we require a capabilities check for the class chat.
 */
export const ChatCompose: FC<ChatComposeProps> = ({
  className,
  onSend = () => {},
  placeholder,
  persistKey,
  context = "class",
}) => {
  const { canCreateChatMessage } = useCapabilities();
  const isAIPanel = context === "ai";
  const canSendMessage = isAIPanel || canCreateChatMessage;

  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { register, handleSubmit, watch, setValue, reset } = useForm<FormInput>();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  let clearPersisted = () => {};
  // `persistKey` MUST be a stable string that does not change during the component's lifetime.
  if (persistKey) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const actions = useFormPersist(persistKey, {
      watch,
      setValue,
      storage: window.localStorage,
    });
    clearPersisted = actions.clear;
  }

  const uploadFile = useCallback(async (file: File) => {
    const id = getNextFileId();
    const pendingFile: PendingFile = { id, file, uploading: true };
    setPendingFiles((prev) => [...prev, pendingFile]);

    try {
      const { url } = await collaborationUploadFile(file);
      setPendingFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, url, uploading: false } : f)),
      );
    } catch (err) {
      rollbarAndLogError("AI chat file upload failed", err);
      setPendingFiles((prev) => prev.filter((f) => f.id !== id));
      toastWarningMessage(t("chat.upload_failed"));
    }
  }, []);

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      for (const file of acceptedFiles) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toastWarningMessage(t("chat.file_too_large", { maxSize: MAX_FILE_SIZE_MB }));
        } else {
          uploadFile(file);
        }
      }
    },
    [uploadFile],
  );

  const removePendingFile = useCallback((id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_AI_FILE_TYPES,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: handleFileDrop,
    onDropRejected: () => {
      toastWarningMessage(t("chat.unsupported_file_type"));
    },
  });

  const readyFiles = pendingFiles.filter((f) => f.url);
  const hasUploading = pendingFiles.some((f) => f.uploading);

  const onSubmit = handleSubmit(({ text }) => {
    const files: AIServerFileType[] = readyFiles.map((f) => ({
      url: f.url!,
      content_type: f.file.type,
      name: f.file.name,
    }));
    onSend(text, files.length > 0 ? files : undefined);
    setPendingFiles([]);
    clearPersisted();
    reset();
  });

  const text = watch("text", "");
  const canSubmit = (text !== "" || readyFiles.length > 0) && !hasUploading;

  const dropzoneProps = isAIPanel ? getRootProps() : {};

  return (
    <div
      {...dropzoneProps}
      className={twMerge(
        "flex w-full flex-col rounded-lg border border-black-tint-70 p-1",
        isDragActive && isAIPanel && "border-blue bg-blue-tint-95",
        className,
      )}
    >
      {isAIPanel && <input {...getInputProps()} ref={fileInputRef} />}

      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2 pb-1 pt-1.5">
          {pendingFiles.map((pf) => (
            <AttachmentChip
              file={pf.file}
              key={pf.id}
              name={pf.file.name}
              uploading={pf.uploading}
              onRemove={() => removePendingFile(pf.id)}
            />
          ))}
        </div>
      )}

      <form
        className="flex w-full items-center justify-center gap-1"
        ref={formRef}
        onSubmit={onSubmit}
      >
        <TextareaAutosize
          {...register("text", {
            required: pendingFiles.length === 0,
            disabled: !canSendMessage,
          })}
          className={twMerge("ml-2 grow resize-none", !canSendMessage && "italic")}
          maxRows={5}
          minRows={1}
          placeholder={canSendMessage ? placeholder : t("chat.join_class_to_chat")}
          wrap="soft"
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              formRef.current?.requestSubmit();
              event.preventDefault();
            }
          }}
        />
        {isAIPanel && (
          <Button
            aria-label={t("chat.attach_file")}
            className="text-black-tint-40 hover:text-black"
            kind="squareLegacy"
            size="l"
            type="button"
            isSquare
            onClick={() => fileInputRef.current?.click()}
          >
            <PaperClipLinkIcon size="l" />
          </Button>
        )}
        <Button
          aria-label={t("chat.send_message")}
          className="bg-blue-tint-95 text-blue"
          disabled={!canSubmit}
          kind="squareLegacy"
          size="l"
          type="submit"
          isSquare
        >
          <ArrowRightIcon size="l" />
        </Button>
      </form>
    </div>
  );
};
